import React, { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, formatErr, API } from "@/lib/api";
import { toast } from "sonner";
import { Building2, Loader2, ScanBarcode, Smartphone, MessageCircle, ShieldCheck, Package, DollarSign, BarChart3, Sparkles, Download as DownloadIcon, ShoppingCart, FileText, Wallet, Users, Globe, CreditCard, WifiOff, Landmark, UserPlus, MonitorDown, CloudDownload, Store } from "lucide-react";
import AISupportChat from "@/components/AISupportChat";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { Link } from "react-router-dom";

export default function Login() {
  const { user, login } = useAuth();
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [totpRequired, setTotpRequired] = useState(false);
  const [useRecovery, setUseRecovery] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [requestOpen, setRequestOpen] = useState(false);
  const [reqForm, setReqForm] = useState({ name: "", email: "", phone: "", company: "", country: "", notes: "" });
  const [reqBusy, setReqBusy] = useState(false);
  const [reqSubmitted, setReqSubmitted] = useState(false);
  const [hideBanner, setHideBanner] = useState(typeof window !== "undefined" && !!localStorage.getItem("hide_install_banner"));
  const [cloudImportOpen, setCloudImportOpen] = useState(false);
  const [cloudImportBusy, setCloudImportBusy] = useState(false);
  const [cloudUrl, setCloudUrl] = useState("");
  const [cloudTotp, setCloudTotp] = useState("");
  const [cloudCompanies, setCloudCompanies] = useState([]);
  const [cloudCompanyId, setCloudCompanyId] = useState("");
  const [googleLoginEnabled, setGoogleLoginEnabled] = useState(false);
  const [googleMfaMode, setGoogleMfaMode] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const activation = params.get("activation");
    const cloud = params.get("cloud_url");
    if (!activation || !cloud) return;
    setCloudUrl(cloud);
    setCloudImportBusy(true);
    api.post("/offline/import-cloud", { cloud_url: cloud, activation_token: activation })
      .then(({ data }) => {
        localStorage.setItem("access_token", data.token);
        localStorage.setItem("company_id", data.company.id);
        toast.success("تم تفعيل النسخة المحلية تلقائياً");
        window.history.replaceState({}, "", "/login");
        window.location.replace("/");
      })
      .catch(err => toast.error(formatErr(err)))
      .finally(() => setCloudImportBusy(false));
  }, []);

  useEffect(() => {
    api.get("/auth/providers")
      .then(({ data }) => setGoogleLoginEnabled(Boolean(data?.google)))
      .catch(() => setGoogleLoginEnabled(false));
    const params = new URLSearchParams(window.location.search);
    const error = params.get("google_error");
    const messages = {
      google_not_configured: "تسجيل Google غير مهيأ على الخادم بعد.",
      google_bad_configuration: "إعداد عنوان العودة إلى Google غير صحيح.",
      google_cancelled: "ألغيت تسجيل الدخول عبر Google.",
      google_state_invalid: "تعذر التحقق من جلسة Google. حاول مرة أخرى.",
      google_state_expired: "انتهت مهلة تسجيل الدخول. ابدأ المحاولة من جديد.",
      google_state_expired: "انتهت مهلة تسجيل الدخول. حاول مرة أخرى.",
      google_email_unverified: "تعذر تأكيد البريد الإلكتروني من Google.",
      google_link_requires_password: "سجّل دخولك بكلمة المرور أولاً؛ لا يمكن ربط هذا البريد تلقائياً.",
      google_account_conflict: "حساب Google مرتبط بحساب آخر أو يتعارض مع بيانات الحساب.",
      google_account_inactive: "الحساب غير مفعّل. يرجى التواصل مع الإدارة.",
      google_trial_expired: "انتهت الفترة التجريبية. يرجى التواصل مع الإدارة.",
      google_auth_failed: "تعذر إكمال تسجيل الدخول عبر Google. حاول مرة أخرى.",
    };
    if (error) toast.error(messages[error] || "فشل تسجيل الدخول عبر Google.");
    if (params.get("google_mfa") === "1") {
      setGoogleMfaMode(true);
      setTotpRequired(true);
      toast.info("تم التحقق من Google. أكمل رمز المصادقة الثنائية.");
    }
    if (error || params.has("google_mfa")) window.history.replaceState({}, "", window.location.pathname);
  }, []);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (googleMfaMode) {
        await api.post("/auth/google/complete", {
          totp_code: !useRecovery ? totpCode : undefined,
          recovery_code: useRecovery ? recoveryCode : undefined,
        });
        toast.success("تم تسجيل الدخول عبر Google");
        window.location.replace("/");
        return;
      }
      await login(email, password, totpRequired && !useRecovery ? totpCode : undefined, totpRequired && useRecovery ? recoveryCode : undefined);
      toast.success("تم تسجيل الدخول");
    } catch (err) {
      const msg = formatErr(err);
      // Server signals TOTP required via detail = "TOTP_REQUIRED"
      if (msg && msg.includes("TOTP_REQUIRED")) {
        setTotpRequired(true);
        toast.info("أدخل رمز المصادقة الثنائية");
      } else {
        toast.error(msg);
        if (totpRequired) setTotpCode("");
      }
    }
    finally { setBusy(false); }
  };

  const dismissBanner = () => { localStorage.setItem("hide_install_banner", "1"); setHideBanner(true); };

  const importCloudAccount = async (e) => {
    e.preventDefault();
    setCloudImportBusy(true);
    try {
      const { data } = await api.post("/offline/import-cloud", {
        cloud_url: cloudUrl, email, password, totp_code: cloudTotp || undefined,
        company_id: cloudCompanyId || undefined,
      });
      localStorage.setItem("access_token", data.token);
      localStorage.setItem("company_id", data.company.id);
      toast.success("تم نقل حساب الشركة إلى هذا الجهاز — سيعمل الآن دون إنترنت");
      window.location.replace("/");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail?.code === "SELECT_COMPANY") {
        setCloudCompanies(detail.companies || []);
        setCloudCompanyId(detail.companies?.[0]?.id || "");
        toast.info("اختر الشركة التي تريد تحميلها محليًا");
      } else {
        toast.error(typeof detail === "string" ? detail : formatErr(err));
      }
    } finally { setCloudImportBusy(false); }
  };

  const submitRequest = async (e) => {
    e.preventDefault();
    if (!reqForm.name.trim() || !reqForm.email.trim()) {
      toast.error("الاسم والإيميل مطلوبان");
      return;
    }
    setReqBusy(true);
    try {
      await api.post("/account-requests", reqForm);
      setReqSubmitted(true);
      toast.success("تم إرسال طلبك بنجاح");
    } catch (err) {
      toast.error(formatErr(err));
    } finally { setReqBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background relative">
      <div className="grid md:grid-cols-2 relative">
      <div className="absolute top-3 end-3 z-30"><LanguageSwitcher /></div>
      <div className="hidden md:flex flex-col justify-between p-10 bg-primary text-primary-foreground relative overflow-hidden">
        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-md bg-white grid place-items-center border border-white/20 overflow-hidden"><img src="/brand/logo-mark.png" alt="محاسبتي" className="w-full h-full object-contain p-0.5" /></div>
            <div>
              <div className="text-xl font-heading font-bold">محاسبتي</div>
              <div className="text-xs opacity-80">إدارة ومحاسبة لكل الأنشطة</div>
            </div>
          </div>
        </div>
        <div className="relative z-10 space-y-6">
          <h1 className="text-4xl font-heading font-bold leading-tight">
            {t("login_headline_1")}<br />{t("login_headline_2")}
          </h1>
          <p className="text-white/85 leading-loose max-w-md text-sm md:text-base">
            أساس «محاسبتي» إدارة مالية ومحاسبية كاملة — إدارة أصناف بالباركود، بيع وشراء نقدي وآجل، صناديق نقدية متعددة العملات،
            كشوف حسابات، قيود محاسبية مزدوجة، مساعد ذكي بالعربية، وتقارير احترافية قابلة للتصدير PDF و Excel.
            وعند فتح كل شركة تختار نشاطها، فتظهر أدواته الخاصة وحده — كل شركة معزولة تماماً والصلاحيات دقيقة لكل موظف.
          </p>
          <div className="grid grid-cols-2 gap-3 max-w-md">
            {[
              [Package, "متعدد الشركات", "فصل كامل بين الفروع"],
              [ScanBarcode, "باركود سريع", "USB + كاميرا الهاتف"],
              [DollarSign, "متعدد العملات", "31 عملة + صرف تلقائي"],
              [BarChart3, "تقارير احترافية", "PDF و Excel"],
              [Sparkles, "مساعد ذكي", "يجاوب ويُنفّذ الأوامر"],
              [ShieldCheck, "أمان وصلاحيات", "دقيقة لكل موظف"],
            ].map(([Icon, t, s], i) => (
              <div key={i} className="border border-white/15 rounded-md p-3 bg-white/5">
                <Icon className="w-4 h-4 mb-1 opacity-80" />
                <div className="text-sm font-bold">{t}</div>
                <div className="text-xs opacity-75 mt-1">{s}</div>
              </div>
            ))}
          </div>
          <Button onClick={()=>setChatOpen(true)} variant="secondary" size="lg" className="gap-2" data-testid="open-chat-button">
            <MessageCircle className="w-4 h-4" /> {t("ai_chat_button")}
          </Button>
          <Link to="/download" className="inline-flex items-center gap-1.5 text-sm text-white/90 hover:text-white underline underline-offset-4 ms-3" data-testid="go-download-link">
            <DownloadIcon className="w-3.5 h-3.5" /> {t("download_app")}
          </Link>
        </div>
        <div className="relative z-10 text-xs opacity-60">© محاسبتي — جميع الحقوق محفوظة</div>
        <div className="absolute -bottom-20 -left-20 w-96 h-96 rounded-full bg-white/5"></div>
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-white/5"></div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12">
        <form onSubmit={submit} className="w-full max-w-md space-y-6" data-testid="login-form">
          <div className="md:hidden flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-md bg-white border border-border grid place-items-center overflow-hidden"><img src="/brand/logo-mark.png" alt="محاسبتي" className="w-full h-full object-contain p-0.5" /></div>
            <div className="text-lg font-heading font-bold">محاسبتي</div>
          </div>
          <div>
            <h2 className="text-2xl font-heading font-bold">{googleMfaMode ? "التحقق بخطوتين" : t("login_form_title")}</h2>
            <p className="text-sm text-muted-foreground mt-2">{googleMfaMode ? "أدخل رمز المصادقة الثنائية لإكمال الدخول." : t("login_form_subtitle")}</p>
          </div>
          {!googleMfaMode && <Link to="/marketplace" className="block" data-testid="marketplace-entry-link"><div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 flex items-center gap-3 hover:bg-emerald-100 transition-colors"><div className="w-10 h-10 rounded-lg bg-emerald-600 text-white grid place-items-center"><Store className="w-5 h-5" /></div><div className="flex-1"><div className="font-bold text-emerald-900">المتاجر الإلكترونية</div><div className="text-xs text-emerald-700 mt-0.5">تصفح المتاجر واطلب منتجاتك بدون تسجيل دخول</div></div><ShoppingCart className="w-5 h-5 text-emerald-700" /></div></Link>}
          {!googleMfaMode && <>
            <div className="space-y-2">
              <Label htmlFor="email">{t("email")}</Label>
              <Input id="email" type="email" value={email} onChange={(e)=>setEmail(e.target.value)} required data-testid="login-email-input" className="h-11" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t("password")}</Label>
              <Input id="password" type="password" value={password} onChange={(e)=>setPassword(e.target.value)} required data-testid="login-password-input" className="h-11" />
            </div>
          </>}
          {totpRequired && !useRecovery && (
            <div className="space-y-2" data-testid="login-totp-field">
              <Label htmlFor="totp">رمز المصادقة الثنائية (2FA)</Label>
              <Input
                id="totp"
                type="text"
                inputMode="numeric"
                value={totpCode}
                onChange={(e)=>setTotpCode(e.target.value.replace(/\D/g,"").slice(0,6))}
                required
                autoFocus
                placeholder="000000"
                maxLength={6}
                className="h-11 text-center text-xl tracking-widest font-mono"
                data-testid="login-totp-input"
              />
              <p className="text-xs text-muted-foreground">افتح تطبيق المصادقة على هاتفك وأدخل الرمز الظاهر</p>
              <button type="button" className="text-xs text-primary hover:underline" onClick={()=>{setUseRecovery(true); setTotpCode("");}} data-testid="use-recovery-link">فقدت هاتفك؟ استخدم رمز استرداد</button>
            </div>
          )}
          {totpRequired && useRecovery && (
            <div className="space-y-2" data-testid="login-recovery-field">
              <Label htmlFor="recovery">رمز الاسترداد</Label>
              <Input
                id="recovery"
                type="text"
                value={recoveryCode}
                onChange={(e)=>setRecoveryCode(e.target.value.toUpperCase())}
                required
                autoFocus
                placeholder="XXXX-XXXX-XXXX-XXXX"
                className="h-11 text-center tracking-widest font-mono"
                data-testid="login-recovery-input"
              />
              <button type="button" className="text-xs text-primary hover:underline" onClick={()=>{setUseRecovery(false); setRecoveryCode("");}}>الرجوع لرمز المصادقة</button>
            </div>
          )}
          <Button type="submit" className="w-full h-11 text-base" disabled={busy} data-testid="login-submit-button">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (googleMfaMode ? "تأكيد الدخول" : t("login_button"))}
          </Button>
          {googleMfaMode && <Button type="button" variant="ghost" className="w-full" onClick={() => window.location.replace("/login")}>إلغاء والعودة لتسجيل الدخول</Button>}
          {!googleMfaMode && <>
          <Button type="button" variant="outline" className="w-full h-11 gap-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50" onClick={()=>setCloudImportOpen(true)} data-testid="import-cloud-account-button">
            <CloudDownload className="w-4 h-4" /> تحميل حسابي إلى نسخة Windows المحلية
          </Button>
          <p className="text-[11px] text-muted-foreground text-center">يظهر هذا الخيار داخل نسخة Windows: يدخل مدير الشركة بيانات السحابة مرة واحدة، ثم تُحفظ الشركة محليًا دون إنترنت.</p>
          </>}

          {googleLoginEnabled && !googleMfaMode && (
          <>
          {/* Divider */}
          <div className="relative py-1">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t"></div></div>
            <div className="relative flex justify-center text-xs"><span className="bg-card px-3 text-muted-foreground">أو</span></div>
          </div>

          <Button
            type="button"
            variant="outline"
            className="w-full h-11 gap-2 bg-white hover:bg-slate-50 border-slate-200"
            onClick={() => window.location.assign(`${API}/auth/google/start`)}
            data-testid="google-login-button"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            سجّل الدخول عبر Google
          </Button>
          </>
          )}
          {!googleMfaMode && <Button type="button" variant="outline" className="w-full gap-2 border-primary/40 text-primary hover:bg-primary/5" onClick={()=>{setRequestOpen(true); setReqSubmitted(false);}} data-testid="request-account-button">
            <UserPlus className="w-4 h-4" /> ليس لديك حساب؟ اطلب حساباً جديداً
          </Button>}
          <Button type="button" variant="outline" className="w-full md:hidden gap-2" onClick={()=>setChatOpen(true)} data-testid="open-chat-button-mobile">
            <MessageCircle className="w-4 h-4" /> {t("ai_chat_button")}
          </Button>
          <Link to="/download" className="md:hidden text-center block text-xs text-primary hover:underline mt-2" data-testid="go-download-link-mobile">
            📲 صفحة تحميل التطبيق
          </Link>
          {!hideBanner && (
            <div className="hidden md:flex items-center gap-3 border border-border rounded-md p-3 bg-muted/40 mt-4 relative" data-testid="install-qr-banner">
              <button type="button" onClick={dismissBanner} className="absolute top-1 left-1 text-muted-foreground hover:text-foreground text-xs w-6 h-6 rounded hover:bg-accent" data-testid="dismiss-install-banner" title="إخفاء">✕</button>
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=140x140&margin=4&color=1E3A8A&data=${encodeURIComponent(typeof window !== "undefined" ? window.location.origin : "")}`}
                alt="QR" width="72" height="72"
                className="rounded bg-white p-1 border border-border shrink-0"
              />
              <div className="text-xs">
                <div className="flex items-center gap-1 font-bold mb-1"><Smartphone className="w-3.5 h-3.5" /> ثبّت التطبيق على هاتفك</div>
                <div className="text-muted-foreground leading-relaxed">امسح الباركود بكاميرا الهاتف، ثم اضغط «تثبيت» من قائمة المتصفح.</div>
              </div>
            </div>
          )}
        </form>
      </div>

      <AISupportChat open={chatOpen} onClose={()=>setChatOpen(false)} />

      <Dialog open={cloudImportOpen} onOpenChange={setCloudImportOpen}>
        <DialogContent className="max-w-lg" data-testid="cloud-import-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><MonitorDown className="w-5 h-5 text-emerald-600" /> تحميل حساب الشركة إلى هذا الجهاز</DialogTitle>
          </DialogHeader>
          <form onSubmit={importCloudAccount} className="space-y-4">
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900 leading-relaxed">
              اكتب بيانات حساب <b>مدير الشركة</b> في السحابة. سيتم نقل الشركة وبياناتها إلى قاعدة محلية على هذا الكمبيوتر فقط. لا تُحفظ كلمة مرور السحابة بعد انتهاء العملية.
            </div>
            <div className="space-y-2"><Label>رابط السحابة</Label><Input value={cloudUrl} onChange={(e)=>setCloudUrl(e.target.value)} placeholder="https://your-domain.com" required dir="ltr" data-testid="cloud-url-input" /></div>
            <div className="space-y-2"><Label>إيميل مدير الشركة</Label><Input type="email" value={email} onChange={(e)=>setEmail(e.target.value)} required dir="ltr" data-testid="cloud-email-input" /></div>
            <div className="space-y-2"><Label>كلمة المرور</Label><Input type="password" value={password} onChange={(e)=>setPassword(e.target.value)} required dir="ltr" data-testid="cloud-password-input" /></div>
            <div className="space-y-2"><Label>رمز 2FA (إن كان مفعّلًا)</Label><Input value={cloudTotp} onChange={(e)=>setCloudTotp(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" maxLength={6} placeholder="اختياري" dir="ltr" data-testid="cloud-totp-input" /></div>
            {cloudCompanies.length > 0 && <div className="space-y-2"><Label>اختر الشركة</Label><select value={cloudCompanyId} onChange={(e)=>setCloudCompanyId(e.target.value)} className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" data-testid="cloud-company-select">{cloudCompanies.map((c)=><option key={c.id} value={c.id}>{c.name} · {c.base_currency || ""}</option>)}</select></div>}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={()=>setCloudImportOpen(false)}>إلغاء</Button>
              <Button type="submit" disabled={cloudImportBusy} className="gap-2 bg-emerald-600 hover:bg-emerald-700" data-testid="cloud-import-submit">{cloudImportBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CloudDownload className="w-4 h-4" />} استيراد حسابي وتشغيل المحلي</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={requestOpen} onOpenChange={(o)=>{setRequestOpen(o); if(!o){setReqSubmitted(false); setReqForm({name:"",email:"",phone:"",company:"",country:"",notes:""});}}}>
        <DialogContent className="max-w-lg" data-testid="request-account-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-primary" /> طلب حساب جديد على محاسبتي
            </DialogTitle>
          </DialogHeader>
          {reqSubmitted ? (
            <div className="py-8 text-center space-y-4">
              <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 dark:bg-emerald-900/30 grid place-items-center">
                <ShieldCheck className="w-8 h-8 text-emerald-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold">شكراً لك — تم استلام طلبك</h3>
                <p className="text-sm text-muted-foreground mt-2 leading-6">
                  سيتواصل معك المدير العام الرئيسي خلال ساعات قليلة عبر الإيميل أو الهاتف لتزويدك ببيانات الدخول.
                </p>
                <p className="text-xs text-muted-foreground mt-3">للاستفسار المباشر، يمكنك مراسلة الإدارة عبر الدردشة الفورية الآن.</p>
              </div>
              <div className="flex gap-2 justify-center">
                <Button variant="outline" onClick={()=>setRequestOpen(false)}>حسناً</Button>
                <Button onClick={()=>{setRequestOpen(false); setChatOpen(true);}} className="gap-1">
                  <MessageCircle className="w-4 h-4" /> فتح الدردشة الآن
                </Button>
              </div>
            </div>
          ) : (
            <form onSubmit={submitRequest} className="space-y-3">
              <p className="text-xs text-muted-foreground bg-muted/50 p-2 rounded">
                املأ البيانات وسيراجعها المدير العام. عند الموافقة سيرسل لك بيانات الدخول عبر الإيميل والواتساب.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>الاسم الكامل *</Label><Input value={reqForm.name} onChange={(e)=>setReqForm({...reqForm, name: e.target.value})} required data-testid="req-name-input" /></div>
                <div><Label>الإيميل *</Label><Input type="email" value={reqForm.email} onChange={(e)=>setReqForm({...reqForm, email: e.target.value})} required data-testid="req-email-input" /></div>
                <div><Label>الهاتف / واتساب</Label><Input value={reqForm.phone} onChange={(e)=>setReqForm({...reqForm, phone: e.target.value})} placeholder="+963 ..." data-testid="req-phone-input" /></div>
                <div><Label>البلد</Label><Input value={reqForm.country} onChange={(e)=>setReqForm({...reqForm, country: e.target.value})} placeholder="سوريا / تركيا / ..." data-testid="req-country-input" /></div>
                <div className="col-span-2"><Label>اسم الشركة / المؤسسة</Label><Input value={reqForm.company} onChange={(e)=>setReqForm({...reqForm, company: e.target.value})} data-testid="req-company-input" /></div>
                <div className="col-span-2">
                  <Label>ملاحظات / نبذة عن نشاطك</Label>
                  <textarea value={reqForm.notes} onChange={(e)=>setReqForm({...reqForm, notes: e.target.value})} rows={2} maxLength={500} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm resize-none" placeholder="مثال: صيدلية / وكيل مواد غذائية / محاسب مبتدئ ..." data-testid="req-notes-input" />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={()=>setRequestOpen(false)}>إلغاء</Button>
                <Button type="submit" disabled={reqBusy} className="gap-1" data-testid="req-submit-btn">
                  {reqBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                  إرسال الطلب
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
      </div>

      {/* Services / What we offer section */}
      <section className="border-t border-border bg-muted/30 px-6 md:px-12 py-14" data-testid="services-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground">{t("services_title")}</h2>
            <p className="text-sm md:text-base text-muted-foreground mt-3 max-w-2xl mx-auto leading-relaxed">{t("services_subtitle")}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              [ShoppingCart, t("svc_pos_t"), t("svc_pos_d"), "bg-emerald-50 text-emerald-600 border-emerald-200"],
              [Package, t("svc_inv_t"), t("svc_inv_d"), "bg-blue-50 text-blue-600 border-blue-200"],
              [FileText, t("svc_invc_t"), t("svc_invc_d"), "bg-indigo-50 text-indigo-600 border-indigo-200"],
              [Landmark, t("svc_acc_t"), t("svc_acc_d"), "bg-amber-50 text-amber-600 border-amber-200"],
              [Wallet, t("svc_bank_t"), t("svc_bank_d"), "bg-teal-50 text-teal-600 border-teal-200"],
              [Users, t("svc_cust_t"), t("svc_cust_d"), "bg-rose-50 text-rose-600 border-rose-200"],
              [Globe, t("svc_curr_t"), t("svc_curr_d"), "bg-cyan-50 text-cyan-600 border-cyan-200"],
              [BarChart3, t("svc_reports_t"), t("svc_reports_d"), "bg-violet-50 text-violet-600 border-violet-200"],
              [Sparkles, t("svc_ai_t"), t("svc_ai_d"), "bg-fuchsia-50 text-fuchsia-600 border-fuchsia-200"],
              [Building2, t("svc_multi_t"), t("svc_multi_d"), "bg-orange-50 text-orange-600 border-orange-200"],
              [CreditCard, t("svc_subs_t"), t("svc_subs_d"), "bg-green-50 text-green-600 border-green-200"],
              [WifiOff, t("svc_pwa_t"), t("svc_pwa_d"), "bg-slate-50 text-slate-600 border-slate-200"],
            ].map(([Icon, title, desc, tone], i) => (
              <div key={i} className="border border-border rounded-lg p-5 bg-card hover:border-primary/40 hover:shadow-md transition-all group" data-testid={`svc-card-${i}`}>
                <div className={`w-11 h-11 rounded-md grid place-items-center border ${tone}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="mt-4 font-heading font-bold text-base text-foreground">{title}</div>
                <div className="text-xs text-muted-foreground mt-2 leading-relaxed">{desc}</div>
              </div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <p className="text-sm text-muted-foreground">{t("services_cta")}</p>
            <Button onClick={()=>setChatOpen(true)} size="lg" className="mt-4 gap-2" data-testid="services-cta-chat">
              <MessageCircle className="w-4 h-4" /> {t("ai_chat_button")}
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
